import React, { useState, useEffect } from 'react';
import { ScrollView, StyleSheet, View, Alert } from 'react-native';
import { Text, Card, Divider, List } from 'react-native-paper';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { usePatientForm } from '@/contexts/PatientFormContext';
import { useFinalizePatientFormMutation } from '@/data/data';
import PatientAuthService from '@/services/PatientAuthService';
import BaseContainer from '@/components/BaseContainer';
import DateUtils from '@/utils/DateUtils';
import { useTranslation } from 'react-i18next';
import Button from '@/components/Button';

export default function ConfirmationScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const { formData, saveProgress, patientName, validateCurrentStep } = usePatientForm();

  const [finalizeForm] = useFinalizePatientFormMutation({
    fetchPolicy: 'network-only',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    // Validation finale
    const validation = validateCurrentStep();
    if (!validation.isValid) {
      setValidationErrors(validation.errors);
    } else {
      setValidationErrors({});
    }
  }, [validateCurrentStep]);

  const handlePrevious = async () => {
    await saveProgress();
    router.back();
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);

      // Validation finale
      const validation = validateCurrentStep();
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        Alert.alert(t('patient.validationFailed'), t('patient.correctErrors'), [{ text: 'OK' }]);
        return;
      }

      // Récupérer le token et les données patient
      const tokenData = await PatientAuthService.getTokenRaw();
      if (!tokenData) {
        throw new Error(t('patient.tokenNotFound'));
      }

      // Préparer les données patient
      const patientData = {
        nom: formData.form.nom,
        prenom: formData.form.prenom,
        sexe: formData.form.sexe === 'M' ? 1 : 2,
        birthday: formData.form.birthday,
        size: formData.form.taille,
        photicEffect: formData.form.photicEffect,
        glasses: formData.form.glasses,
      };

      // Préparer les données habitudes
      const habitudesData = {
        patientId: tokenData.patientId,
        heureReveil: formData.form.heureReveil
          ? DateUtils.getTimeSQLFormat(formData.form.heureReveil)
          : null,
        heureCoucher: formData.form.heureCoucher
          ? DateUtils.getTimeSQLFormat(formData.form.heureCoucher)
          : null,
        tempsSieste: formData.form.tempsSieste
          ? DateUtils.getTimeSQLFormat(formData.form.tempsSieste)
          : null,
        tempsLecture: formData.form.tempsLecture
          ? DateUtils.getTimeSQLFormat(formData.form.tempsLecture)
          : null,
        tempsTablette: formData.form.tempsTablette
          ? DateUtils.getTimeSQLFormat(formData.form.tempsTablette)
          : null,
        tempsSmartphone: formData.form.tempsSmartphone
          ? DateUtils.getTimeSQLFormat(formData.form.tempsSmartphone)
          : null,
        tempsAutreVisionPres: formData.form.tempsAutreVisionPres
          ? DateUtils.getTimeSQLFormat(formData.form.tempsAutreVisionPres)
          : null,
        tempsDiscussion: formData.form.tempsDiscussion
          ? DateUtils.getTimeSQLFormat(formData.form.tempsDiscussion)
          : null,
        tempsJeuxSociete: formData.form.tempsJeuxSociete
          ? DateUtils.getTimeSQLFormat(formData.form.tempsJeuxSociete)
          : null,
        tempsOrdinateur: formData.form.tempsOrdinateur
          ? DateUtils.getTimeSQLFormat(formData.form.tempsOrdinateur)
          : null,
        tempsCuisine: formData.form.tempsCuisine
          ? DateUtils.getTimeSQLFormat(formData.form.tempsCuisine)
          : null,
        tempsConduite: formData.form.tempsConduite
          ? DateUtils.getTimeSQLFormat(formData.form.tempsConduite)
          : null,
        tempsTelevision: formData.form.tempsTelevision
          ? DateUtils.getTimeSQLFormat(formData.form.tempsTelevision)
          : null,
      };

      // Préparer les activités préférentielles
      const activitesPreferentielles = (formData.form.activitesPreferentielles || [])
        .filter((a) => a.nom && a.nom.trim() !== '')
        .map((a) => ({
          patientId: tokenData.patientId,
          nom: a.nom,
          distanceMin: a.distanceMin ? parseFloat(a.distanceMin) : null,
          distanceMax: a.distanceMax ? parseFloat(a.distanceMax) : null,
        }));

      // Appeler la mutation de finalisation
      await finalizeForm({
        variables: {
          patientId: tokenData.patientId,
          tokenId: tokenData.tokenId,
          patientData,
          habitudesData,
          activitesPreferentielles,
        },
      });

      // Rediriger vers la page de succès
      router.replace('/patient/form/success');
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      Alert.alert(t('patient.error'), t('patient.technicalError') + '\n\n' + error.toString(), [
        { text: 'OK' },
      ]);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderBiometrieData = () => {
    const formatSexe = (sexe?: string | 'M' | 'F') => {
      if (sexe === 'M' || sexe === 1) return t('patient.male');
      if (sexe === 'F' || sexe === 2) return t('patient.female');
      return '-';
    };

    const formatDate = (date?: Date | string) => {
      if (!date) return '-';
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleDateString('fr-FR');
    };

    return (
      <Card style={styles.dataCard}>
        <Card.Title
          title={t('patient.biometricData')}
          left={() => <List.Icon icon="account" size={24} color="#678aae" />}
        />
        <Card.Content>
          {formData.form.nom && formData.form.prenom && (
            <List.Item
              title={t('patient.name')}
              description={`${formData.form.prenom} ${formData.form.nom}`}
              left={(props) => <List.Icon {...props} icon="account-circle" />}
            />
          )}

          {formData.form.sexe && (
            <List.Item
              title={t('patient.gender')}
              description={formatSexe(formData.form.sexe)}
              left={(props) => <List.Icon {...props} icon="gender-male-female" />}
            />
          )}

          {formData.form.birthday && (
            <List.Item
              title={t('patient.birthdate')}
              description={formatDate(formData.form.birthday)}
              left={(props) => <List.Icon {...props} icon="calendar" />}
            />
          )}

          {formData.form.taille && (
            <List.Item
              title={t('patient.height')}
              description={`${formData.form.taille} cm`}
              left={(props) => <List.Icon {...props} icon="human-male-height" />}
            />
          )}

          <Divider style={styles.divider} />

          <List.Item
            title={t('patient.acceptPhoticEffects')}
            description={formData.form.photicEffect ? t('patient.yes') : t('patient.no')}
            left={(props) => <List.Icon {...props} icon="lightbulb-outline" />}
          />
          <List.Item
            title={t('patient.wantGlassesIndependence')}
            description={formData.form.glasses ? t('patient.yes') : t('patient.no')}
            left={(props) => <List.Icon {...props} icon="glasses" />}
          />
        </Card.Content>
      </Card>
    );
  };

  const renderHabitudesData = () => {
    // Debug: afficher les activités préférentielles
    console.log(
      'Activités préférentielles:',
      JSON.stringify(formData.form.activitesPreferentielles, null, 2)
    );

    // Helper pour formater l'heure
    const formatTime = (date: Date | string | undefined): string => {
      if (!date) return '';
      const d = date instanceof Date ? date : new Date(date);
      return d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    const totalMinutes = Object.entries(formData.form)
      .filter(([key]) => key.startsWith('temps'))
      .reduce((sum, [, value]) => {
        if (!value) return sum;
        return sum + DateUtils.getMinutes(value as Date);
      }, 0);

    return (
      <Card style={styles.dataCard}>
        <Card.Title
          title={t('patient.visualHabits')}
          left={() => <List.Icon icon="clock-outline" size={24} color="#678aae" />}
        />
        <Card.Content>
          {formData.form.heureReveil && (
            <List.Item
              title={t('patient.wakeupHour')}
              description={formatTime(formData.form.heureReveil)}
              left={(props) => <List.Icon {...props} icon="weather-sunny" />}
            />
          )}

          {formData.form.heureCoucher && (
            <List.Item
              title={t('patient.bedtimeHour')}
              description={formatTime(formData.form.heureCoucher)}
              left={(props) => <List.Icon {...props} icon="weather-night" />}
            />
          )}

          {formData.form.tempsSieste && (
            <List.Item
              title={t('patient.napTime').replace(' par jour', '')}
              description={formatTime(formData?.form?.tempsSieste || '00:00')}
              left={(props) => <List.Icon {...props} icon="power-sleep" />}
            />
          )}

          <Divider style={styles.divider} />

          <List.Subheader>Vision de près</List.Subheader>

          <List.Item
            title={t('patient.readingTime').replace(' par jour', '')}
            description={DateUtils.displayHRTimeWithH(
              DateUtils.getMinutes(formData?.form?.tempsLecture)
            )}
            left={(props) => <List.Icon {...props} icon="book-open" />}
          />

          <List.Item
            title={t('patient.tabletTime').replace(' par jour', '')}
            description={DateUtils.displayHRTimeWithH(
              DateUtils.getMinutes(formData?.form?.tempsTablette)
            )}
            left={(props) => <List.Icon {...props} icon="tablet" />}
          />

          <List.Item
            title={t('patient.smartphoneTime').replace(' par jour', '')}
            description={DateUtils.displayHRTimeWithH(
              DateUtils.getMinutes(formData?.form?.tempsSmartphone)
            )}
            left={(props) => <List.Icon {...props} icon="cellphone" />}
          />

          <List.Item
            title={t('patient.otherNearVisionTime').replace(' par jour', '')}
            description={DateUtils.displayHRTimeWithH(
              DateUtils.getMinutes(formData?.form?.tempsAutreVisionPres)
            )}
            left={(props) => <List.Icon {...props} icon="eye" />}
          />

          <Divider style={styles.divider} />

          <List.Subheader>Vision intermédiaire</List.Subheader>

          <List.Item
            title={t('patient.discussionTime').replace(' par jour', '')}
            description={DateUtils.displayHRTimeWithH(
              DateUtils.getMinutes(formData?.form?.tempsDiscussion)
            )}
            left={(props) => <List.Icon {...props} icon="account-group" />}
          />

          <List.Item
            title={t('patient.boardGamesTime').replace(' par jour', '')}
            description={DateUtils.displayHRTimeWithH(
              DateUtils.getMinutes(formData?.form?.tempsJeuxSociete)
            )}
            left={(props) => <List.Icon {...props} icon="dice-multiple" />}
          />

          <List.Item
            title={t('patient.computerTime').replace(' par jour', '')}
            description={DateUtils.displayHRTimeWithH(
              DateUtils.getMinutes(formData?.form?.tempsOrdinateur)
            )}
            left={(props) => <List.Icon {...props} icon="laptop" />}
          />

          <List.Item
            title={t('patient.cookingTime').replace(' par jour', '')}
            description={DateUtils.displayHRTimeWithH(
              DateUtils.getMinutes(formData?.form?.tempsCuisine)
            )}
            left={(props) => <List.Icon {...props} icon="chef-hat" />}
          />

          <Divider style={styles.divider} />

          <List.Subheader>Vision de loin</List.Subheader>

          <List.Item
            title={t('patient.drivingTime').replace(' par jour', '')}
            description={DateUtils.displayHRTimeWithH(
              DateUtils.getMinutes(formData?.form?.tempsConduite)
            )}
            left={(props) => <List.Icon {...props} icon="car" />}
          />

          <List.Item
            title={t('patient.tvTime').replace(' par jour', '')}
            description={DateUtils.displayHRTimeWithH(
              DateUtils.getMinutes(formData?.form?.tempsTelevision)
            )}
            left={(props) => <List.Icon {...props} icon="television" />}
          />

          <Divider style={styles.divider} />

          <List.Subheader>Total</List.Subheader>
          <Text style={styles.totalTime}>
            Temps d'éveil total: {DateUtils.displayHRTimeWithH(totalMinutes)} par jour
          </Text>

          {formData.form.activitesPreferentielles &&
          formData.form.activitesPreferentielles.length > 0 ? (
            <>
              <Divider style={styles.divider} />
              <List.Subheader>{t('patient.preferredActivities')}</List.Subheader>
              {formData.form.activitesPreferentielles
                .filter((activite) => activite && activite.nom && activite.nom.trim() !== '')
                .map((activite, index) => (
                  <List.Item
                    key={index}
                    title={activite.nom}
                    description={`${activite.distanceMin || '?'} - ${activite.distanceMax || '?'} m`}
                    left={(props) => <List.Icon {...props} icon="star" />}
                  />
                ))}
            </>
          ) : null}
        </Card.Content>
      </Card>
    );
  };

  return (
    <BaseContainer
      withScrollView={'keyboardAware'}
      outerViewProps={{ style: { backgroundColor: '#fff' } }}
    >
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
          {/* Message de confirmation */}
          <Card style={styles.summaryCard}>
            <Card.Content>
              <View style={styles.summaryHeader}>
                <List.Icon icon="check-circle" size={48} color="#4CAF50" />
                <Text style={styles.summaryTitle}>{t('patient.dataRecap')}</Text>
              </View>

              <Text style={styles.summaryDescription}>{t('patient.reviewBeforeSubmit')}</Text>

              {Object.keys(validationErrors).length > 0 && (
                <View style={styles.errorsContainer}>
                  <Text style={styles.errorTitle}>{t('patient.correctErrors')}</Text>
                  {Object.entries(validationErrors).map(([key, error]) => (
                    <Text key={key} style={styles.errorText}>
                      • {error}
                    </Text>
                  ))}
                </View>
              )}
            </Card.Content>
          </Card>

          {/* Données biométriques */}
          {renderBiometrieData()}

          {/* Habitudes visuelles */}
          {renderHabitudesData()}

          {/* Informations importantes */}
          <Card style={styles.infoCard}>
            <Card.Title
              title={t('patient.importantInfo')}
              left={() => <List.Icon icon="information" size={24} color="#2196F3" />}
            />
            <Card.Content>
              <List.Item
                title={t('patient.confidentiality')}
                description={t('patient.confidentialityDesc')}
                left={(props) => <List.Icon {...props} icon="shield-check" />}
              />
              <List.Item
                title={t('patient.modifications')}
                description={t('patient.modificationsDesc')}
                left={(props) => <List.Icon {...props} icon="pencil" />}
              />
              <List.Item
                title={t('patient.followUp')}
                description={t('patient.followUpDesc')}
                left={(props) => <List.Icon {...props} icon="phone" />}
              />
            </Card.Content>
          </Card>

          {/* Actions */}
          <View style={styles.footerContainer}>
            <View style={styles.buttonRow}>
              <Button
                mode="outlined"
                onPress={handlePrevious}
                disabled={isSubmitting}
                outerStyle={{ borderWidth: 0 }}
                contentStyle={{ minWidth: 150 }}
              >
                {t('patient.modify')}
              </Button>

              <Button
                mode="contained"
                onPress={handleSubmit}
                loading={isSubmitting}
                disabled={isSubmitting || Object.keys(validationErrors).length > 0}
                contentStyle={{ minWidth: 150 }}
              >
                {isSubmitting ? t('patient.sending') : t('patient.finalize')}
              </Button>
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    </BaseContainer>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 40,
  },
  summaryCard: {
    marginBottom: 16,
    backgroundColor: '#fff',
  },
  summaryHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  summaryTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginTop: 12,
    textAlign: 'center',
    color: '#333',
  },
  summaryDescription: {
    fontSize: 16,
    lineHeight: 24,
    textAlign: 'center',
    color: '#666',
    marginBottom: 16,
  },
  errorsContainer: {
    backgroundColor: '#ffebee',
    padding: 12,
    borderRadius: 8,
    borderLeftWidth: 4,
    borderLeftColor: '#f44336',
  },
  errorTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#d32f2f',
    marginBottom: 8,
  },
  errorText: {
    fontSize: 14,
    color: '#d32f2f',
    marginBottom: 4,
  },
  dataCard: {
    marginBottom: 16,
    backgroundColor: '#fff',
  },
  divider: {
    marginVertical: 8,
  },
  totalTime: {
    fontSize: 14,
    color: '#678aae',
    fontWeight: '500',
    marginBottom: 8,
    marginLeft: 16,
  },
  infoCard: {
    marginBottom: 16,
    backgroundColor: '#e3f2fd',
  },
  footerContainer: {
    marginTop: 24,
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  buttonRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    flexWrap: 'wrap',
  },
});
